import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gmDisplayName } from '../../utils/displayName';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, ReferenceLine } from 'recharts';
import api from '../../api/api';
import SeasonSelector from '../../components/analytics/SeasonSelector';
import StatCard from '../../components/analytics/StatCard';

const TeamDetail = () => {
  const { t } = useTranslation();
  const { teamId } = useParams();
  const [seasonId, setSeasonId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!seasonId || !teamId) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/analytics/team/${teamId}?seasonId=${seasonId}`);
        setData(res.data);
      } catch (err) { setData(null); }
      setLoading(false);
    };
    fetch();
  }, [seasonId, teamId]);

  if (loading) return <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('common.loading')}</div>;
  if (!data) return <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('analytics.noData')}</div>;

  const { team, stats, splits, trend, matchHistory } = data;
  const tabs = ['overview', 'trends', 'splits', 'history'];

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <Link to="/analytics/team-rankings" className="text-blue-600 dark:text-blue-400 hover:underline text-sm mb-1 inline-block">← {t('analytics.backToRankings')}</Link>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200">{team.city} {team.name}</h1>
          {team.owner && <p className="text-gray-500 dark:text-gray-400 text-sm">{t('analytics.gm')}: {gmDisplayName(team.owner)}</p>}
        </div>
        <SeasonSelector selectedSeasonId={seasonId} onSeasonChange={setSeasonId} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition ${activeTab === tab ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300'}`}
          >
            {t(`analytics.tabs.${tab}`)}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            <StatCard label={t('analytics.cols.record')} value={`${stats.wins}-${stats.losses}`} icon="🏀" color="blue" />
            <StatCard label={t('analytics.cols.pct')} value={stats.winPct.toFixed(3)} icon="📊" color={stats.winPct >= 0.5 ? 'green' : 'red'} />
            <StatCard label={t('analytics.cols.ppg')} value={stats.ppg} icon="🎯" color="blue" />
            <StatCard label={t('analytics.cols.oppg')} value={stats.oppg} icon="🛡️" color="orange" />
            <StatCard label={t('analytics.cols.diff')} value={`${stats.pointsDiff > 0 ? '+' : ''}${stats.pointsDiff}`} icon="📈" color={stats.pointsDiff >= 0 ? 'green' : 'red'} />
            <StatCard label={t('analytics.cols.home')} value={stats.homeRecord} icon="🏠" color="purple" />
            <StatCard label={t('analytics.cols.away')} value={stats.awayRecord} icon="✈️" color="gray" />
            <StatCard label={t('analytics.cols.streak')} value={stats.streak} icon="🔥" color={stats.streak?.startsWith('W') ? 'green' : 'red'} />
            <StatCard label={t('analytics.cols.last5')} value={stats.last5Record} icon="📅" color="blue" />
            <StatCard label={t('analytics.cols.last10')} value={stats.last10Record} icon="📆" color="blue" />
          </div>

          {/* Quick Win% Chart */}
          {trend.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
              <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-4">{t('analytics.charts.winPctOverTime')}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="game" label={{ value: t('analytics.charts.gameNumber'), position: 'insideBottom', offset: -5 }} />
                  <YAxis domain={[0, 1]} tickFormatter={(v) => (v * 100).toFixed(0) + '%'} />
                  <Tooltip formatter={(v) => [(v * 100).toFixed(1) + '%', t('analytics.cols.pct')]} labelFormatter={(v) => `${t('analytics.charts.game')} ${v}`} />
                  <ReferenceLine y={0.5} stroke="#999" strokeDasharray="5 5" />
                  <Line type="monotone" dataKey="winPct" stroke="#3B82F6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Trends Tab */}
      {activeTab === 'trends' && trend.length > 0 && (
        <div className="space-y-8">
          {/* Scoring Trend */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-4">{t('analytics.charts.scoringTrend')}</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="game" />
                <YAxis />
                <Tooltip labelFormatter={(v) => `${t('analytics.charts.game')} ${v}`} />
                <Legend />
                <Line type="monotone" dataKey="rollingPpg" name={t('analytics.cols.ppg')} stroke="#3B82F6" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="rollingOppg" name={t('analytics.cols.oppg')} stroke="#EF4444" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Point Differential Trend */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-4">{t('analytics.charts.diffTrend')}</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="game" />
                <YAxis />
                <Tooltip labelFormatter={(v) => `${t('analytics.charts.game')} ${v} vs ${trend[v-1]?.opponent || ''}`} />
                <ReferenceLine y={0} stroke="#000" />
                <Bar dataKey="diff" fill="#3B82F6" name={t('analytics.cols.diff')}>
                  {trend.map((entry, index) => (
                    <rect key={index} fill={entry.diff >= 0 ? '#22C55E' : '#EF4444'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Game Results */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-4">{t('analytics.charts.gameResults')}</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="game" />
                <YAxis />
                <Tooltip labelFormatter={(v) => `${t('analytics.charts.game')} ${v} vs ${trend[v-1]?.opponent || ''}`} />
                <Legend />
                <Bar dataKey="teamScore" name={t('analytics.cols.scored')} fill="#3B82F6" />
                <Bar dataKey="oppScore" name={t('analytics.cols.allowed')} fill="#EF4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Splits Tab */}
      {activeTab === 'splits' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {['home', 'away'].map(loc => (
            <div key={loc} className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
              <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-4">
                {loc === 'home' ? '🏠' : '✈️'} {t(`analytics.splits.${loc}`)}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <StatCard label={t('analytics.cols.record')} value={`${splits[loc].wins}-${splits[loc].losses}`} color={loc === 'home' ? 'purple' : 'gray'} />
                <StatCard label={t('analytics.cols.gp')} value={splits[loc].gp} color="blue" />
                <StatCard label={t('analytics.cols.ppg')} value={splits[loc].ppg} color="green" />
                <StatCard label={t('analytics.cols.oppg')} value={splits[loc].oppg} color="red" />
                <StatCard label={t('analytics.cols.diff')} value={`${splits[loc].diff > 0 ? '+' : ''}${splits[loc].diff}`} color={splits[loc].diff >= 0 ? 'green' : 'red'} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.cols.date')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.cols.location')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.cols.opponent')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.cols.result')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.cols.score')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.cols.diff')}</th>
              </tr>
            </thead>
            <tbody>
              {matchHistory.map((m, i) => (
                <tr key={i} className="border-b hover:bg-gray-50 dark:bg-gray-800">
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{m.date ? new Date(m.date).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-3 text-sm">{m.isHome ? t('analytics.splits.home') : t('analytics.splits.away')}</td>
                  <td className="px-4 py-3 text-sm font-medium">{m.opponent?.abbreviation || m.opponent?.name || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${m.won ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`}>
                      {m.won ? 'W' : 'L'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold">{m.teamScore} - {m.oppScore}</td>
                  <td className={`px-4 py-3 text-sm font-semibold ${m.diff > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                    {m.diff > 0 ? '+' : ''}{m.diff}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {matchHistory.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('analytics.noData')}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamDetail;
